import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Layout, Select, Progress, Tag, Table, Tooltip } from 'antd';
import { theme, Text, TableComponent, ButtonAnt, InputComponent } from '@pfmam/pfmam';
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  DownloadOutlined,
  TeamOutlined,
  DollarOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Container, Box, Banner } from '../styles/globalStyle';
import AppLayout from '../common/layouts/appLayout';
import NoDataFound from '../common/components/noDataFound';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { GetAllThirdPartyHoldings } from '../actions/actions';

const { Content } = Layout;
const { Option } = Select;

/* ─────────────────────────────────────────
   Table styles (shared)
───────────────────────────────────────── */

const ContentWrapper = styled(Content)`
  .ant-table {
    border-radius: 4px;
    .ant-table-tbody {
      & > tr {
        cursor: default;
        &:hover {
          &.ant-table-row:not(.ant-table-expanded-row) {
            background: linear-gradient(
                0deg,
                ${(props) => props.rgbaHover},
                ${(props) => props.rgbaHover}
              ),
              ${theme.colors.white};
          }
        }
        & > td {
          vertical-align: top;
          padding: 16px 24px;
          &.ant-table-column-sort {
            background: ${theme.colors.transparent};
          }
        }
        &:last-child > td {
          border-bottom-color: ${theme.colors.transparent};
        }
      }
    }
    .ant-table-thead > tr > th {
      background: ${theme.colors.light_grey};
      font-family: 'Inter';
      padding: 0 24px;
      font-weight: normal;
      line-height: 20px;
      &:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
        background-color: ${theme.colors.transparent};
      }
    }
    &.ant-table.ant-table-middle .ant-table-thead {
      .ant-table-column-sorters {
        padding: 0;
        .ant-table-column-sorter-inner .anticon { color: ${theme.colors.grey_stone}; }
        .ant-table-column-sorter-up.active,
        .ant-table-column-sorter-down.active { color: ${theme.colors.black_sorted}; }
      }
      .ant-table-column-sorter-inner { margin-top: 0; }
    }
    .sort-space-none {
      .ant-table-column-sorters { justify-content: flex-start; }
      .ant-table-column-title { flex: 0 0 auto; }
    }
  }
  .ant-table-title { padding: 0 !important; }
  tr.ant-table-expanded-row > td,
  tr.ant-table-expanded-row:hover > td,
  .ant-table-wrapper.table-primary .ant-table-tbody .ant-table-expanded-row > td {
    background: ${theme.colors.grey_faint};
  }
  .ant-table.ant-table-middle .ant-table-tbody .ant-table-wrapper:only-child .ant-table {
    margin: -19px -16px;
  }
  .ant-table-expanded-row .ant-table-tbody > tr > td {
    background: ${theme.colors.grey_faint} !important;
    border-bottom-color: ${theme.colors.grey_fossil};
    padding: 16px 32px 16px 16px;
    line-height: 20px;
  }
  .ant-table-expanded-row .ant-table-tbody > tr:last-child > td {
    border-bottom-color: ${theme.colors.transparent};
  }
`;

/* ─────────────────────────────────────────
   Layout helpers
───────────────────────────────────────── */

const Wrapper = styled.div`
  padding-top: 32px;
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: ${theme.font_size.size_xl};
  color: ${theme.colors.black_secondary};
  font-family: 'Inter', sans-serif;
  line-height: 20px;
  margin-bottom: 24px;
  &:hover { opacity: 0.7; }
`;

/* ─────────────────────────────────────────
   Period selector bar
───────────────────────────────────────── */

const PeriodBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_fossil};
  border-radius: 8px;
  margin-bottom: 24px;
`;

const PeriodLabel = styled.span`
  font-size: ${theme.font_size.size_xl};
  font-weight: 600;
  color: ${theme.colors.black_secondary};
  font-family: 'Inter', sans-serif;
  margin-right: 4px;
`;

/* ─────────────────────────────────────────
   Summary stat cards
───────────────────────────────────────── */

const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_fossil};
  border-radius: 8px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${(props) => props.color || theme.colors.clean_black};
  font-family: 'Inter', sans-serif;
  line-height: 1.2;
`;

const StatSubLabel = styled.div`
  font-size: ${theme.font_size.size_l};
  color: ${theme.colors.grey_tertiary};
  font-family: 'Inter', sans-serif;
  line-height: 18px;
`;

/* ─────────────────────────────────────────
   Status badge
───────────────────────────────────────── */

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: ${theme.font_size.size_l};
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  white-space: nowrap;
  background: ${(props) =>
    props.status === 'submitted'
      ? '#e6f4ea'
      : props.status === 'outstanding'
      ? '#fff3cd'
      : '#fde8e8'};
  color: ${(props) =>
    props.status === 'submitted'
      ? '#1e7e34'
      : props.status === 'outstanding'
      ? '#856404'
      : '#c0392b'};
`;

/* ─────────────────────────────────────────
   Static data
───────────────────────────────────────── */

const TabToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${theme.colors.light_grey};
  border-radius: 8px;
  padding: 3px;
`;

const TabToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: ${theme.font_size.size_l};
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  line-height: 20px;
  transition: background 0.15s ease, color 0.15s ease;
  background: ${(props) => (props.active ? theme.colors.white : 'transparent')};
  color: ${(props) => (props.active ? theme.colors.clean_black : theme.colors.grey_tertiary)};
  box-shadow: ${(props) => (props.active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none')};
  &:hover {
    color: ${theme.colors.clean_black};
    background: ${(props) => (props.active ? theme.colors.white : 'rgba(0,0,0,0.05)')};
  }
`;

const ToolbarControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  min-width: 32px;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  & > span {
    height: 19px;
    min-width: 19px;
    width: 19px;
    margin: 0 auto;
    border: 2px solid ${theme.colors.grey};
    border-radius: 2px;
    padding: 5px;
    position: relative;
    transition: 0.3s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    &::before {
      content: '';
      position: absolute;
      top: 3px;
      background: ${theme.colors.black_secondary};
      height: 10px;
      width: 2px;
      transition: transform 0.3s ease-out;
      transform: rotate(0deg);
    }
    &::after {
      content: '';
      position: absolute;
      top: 7px;
      right: 2.3px;
      background: ${theme.colors.black_secondary};
      height: 2px;
      width: 10px;
      transition: transform 0.3s ease-out;
      transform: rotate(-180deg);
    }
  }
  &:focus > span {
    box-shadow: ${theme.shadows.input_focus};
    border-color: ${theme.colors.blue_primay};
  }
  &.expanded > span {
    &::after { transform: rotate(0deg); }
    &::before { transform: rotate(90deg); }
  }
`;

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1).padStart(2, '0'),
}));

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i);

const formatCurrency = (value) =>
  value != null
    ? `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

/* ─────────────────────────────────────────
   Component
───────────────────────────────────────── */

const holdingColumns = [
  {
    title: '',
    dataIndex: '',
    key: 'indent',
    width: 56,
  },
  {
    title: 'Security Description',
    dataIndex: 'securityDescription',
    key: 'securityDescription',
    render: (val) => <Text label={val || '—'} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />,
  },
  {
    title: 'Account ID',
    dataIndex: 'accountUniqueID',
    key: 'accountUniqueID',
    render: (val) => <Text label={val || '—'} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />,
  },
  {
    title: 'Balance',
    dataIndex: 'balance',
    key: 'balance',
    render: (val) => <Text label={formatCurrency(val)} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />,
  },
  {
    title: 'Holding Status',
    dataIndex: 'holdingStatus',
    key: 'holdingStatus',
    render: (val) => <Text label={val || '—'} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />,
  },
];

const AllThirdPartyHoldingsContainer = () => {
  const primary_color = useSelector((state) => state.siteInfo.primaryColor);
  const rgbaPrimary = useSelector((state) => state.siteInfo.rgbaPrimary);
  const webuserAccessType = useSelector((state) => state.userSlice.webUserType);
  const history = useHistory();

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [activeTab, setActiveTab] = useState('submitted');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [outstandingSearch, setOutstandingSearch] = useState('');
  const [pagination, setPagination] = useState({
    position: ['bottomRight'],
    showQuickJumper: true,
    size: 'default',
    current: 1,
  });
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittedClients, setSubmittedClients] = useState([]);
  const [outstandingClients, setOutstandingClients] = useState([]);

  // Redirect unauthorised users
  useEffect(() => {
    if (webuserAccessType !== 1 && webuserAccessType !== 2) {
      history.replace('/404');
    }
  }, [webuserAccessType, history]);

  const fetchAllClients = () => {
    const yearMonth = parseInt(`${selectedYear}${String(selectedMonth).padStart(2, '0')}`, 10);
    setLoading(true);
    GetAllThirdPartyHoldings(yearMonth)
      .then((response) => {
        const raw = response.data;
        const records = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.Table)
          ? raw.Table
          : Array.isArray(raw?.value)
          ? raw.value
          : Array.isArray(raw?.data)
          ? raw.data
          : [];

        // Group flat rows by SSNORTIN (one row per holding per client)
        const clientMap = {};
        records.forEach((item) => {
          const mapKey = item.SSNORTIN || item.ClientName || String(Math.random());
          if (!clientMap[mapKey]) {
            clientMap[mapKey] = {
              key: mapKey,
              client: item.ClientName || '',
              ssnortin: item.SSNORTIN || '',
              holdings: item.TotalAccounts ?? 0,
              lastModified: item.LastModifiedDate || '',
              submissionStatus: item.SubmissionStatus || '',
              holdingRows: [],
            };
          }
          clientMap[mapKey].holdingRows.push({
            key: `${mapKey}-${clientMap[mapKey].holdingRows.length}`,
            securityDescription: item.SecurityDescription || '',
            accountUniqueID: item.AccountUniqueID || '',
            balance: item.Balance ?? null,
            holdingStatus: item.HoldingStatus || '',
          });
        });

        const allClients = Object.values(clientMap).map((c) => ({
          ...c,
          totalBalance: c.holdingRows.reduce((sum, r) => sum + (r.balance || 0), 0),
        }));

        setSubmittedClients(allClients.filter((c) => c.submissionStatus === 'Complete'));
        setOutstandingClients(allClients.filter((c) => c.submissionStatus === 'Partial' || c.submissionStatus === 'Not Submitted'));
      })
      .catch((error) => {
        console.log('[AllThirdPartyHoldings] error:', error);
        setSubmittedClients([]);
        setOutstandingClients([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAllClients();
  }, [selectedMonth, selectedYear]);

  const totalSubmitted = submittedClients.length;
  const totalClients = submittedClients.length + outstandingClients.length;
  const totalBalance = submittedClients.reduce((sum, c) => sum + c.totalBalance, 0);
  const completionPct = totalClients > 0 ? Math.round((totalSubmitted / totalClients) * 100) : 0;
  const totalOutstandingClients = outstandingClients.length;

  const filteredSubmitted = submittedSearch
    ? submittedClients.filter((r) =>
        r.client.toLowerCase().includes(submittedSearch.toLowerCase()) ||
        r.ssnortin.includes(submittedSearch)
      )
    : submittedClients;

  const filteredOutstanding = outstandingSearch
    ? outstandingClients.filter((r) =>
        r.client.toLowerCase().includes(outstandingSearch.toLowerCase()) ||
        r.ssnortin.includes(outstandingSearch)
      )
    : outstandingClients;

  const finalSubmitted = filteredSubmitted;
  const finalOutstanding = filteredOutstanding;

  const handleDownload = () => {
    const allClients = [
      ...submittedClients,
      ...outstandingClients,
    ];
    if (allClients.length === 0) return;

    const headers = ['Client Name', 'Submission Status', 'Total Accounts', 'Security Description', 'Account Unique ID', 'Balance', 'Last Modified Date', 'Holding Status'];
    const rows = [];
    allClients.forEach((client) => {
      if (client.holdingRows && client.holdingRows.length > 0) {
        client.holdingRows.forEach((holding) => {
          rows.push([
            client.client,
            client.submissionStatus ?? '',
            client.holdings ?? '',
            holding.securityDescription ?? '',
            holding.accountUniqueID ?? '',
            holding.balance != null ? holding.balance : '',
            client.lastModified ?? '',
            holding.holdingStatus ?? '',
          ]);
        });
      } else {
        rows.push([
          client.client,
          client.submissionStatus ?? '',
          client.holdings ?? '',
          '',
          '',
          '',
          client.lastModified ?? '',
          '',
        ]);
      }
    });

    const csv = [headers, ...rows]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `all-third-party-holdings-${selectedYear}${String(selectedMonth).padStart(2,'0')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── Table columns ──

  const submittedColumns = [
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'client',
      sorter: (a, b) => a.client.localeCompare(b.client),
      className: 'sort-space-none',
      render: (text, record) => (
        <div>
          <Text label={text} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />
          <Text label={record.ssnortin} fontSize={`${theme.font_size.size_l}`} color={`${theme.colors.grey_tertiary}`} lineheight="18px" />
        </div>
      ),
    },
    {
      title: '# Holdings',
      dataIndex: 'holdings',
      key: 'holdings',
      sorter: (a, b) => a.holdings - b.holdings,
      className: 'sort-space-none',
      render: (val) => <Text label={String(val)} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />,
    },
    {
      title: 'Total Balance',
      dataIndex: 'totalBalance',
      key: 'totalBalance',
      sorter: (a, b) => a.totalBalance - b.totalBalance,
      defaultSortOrder: 'descend',
      className: 'sort-space-none',
      render: (val) => <Text label={formatCurrency(val)} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />,
    },
    {
      title: 'Last Modified',
      dataIndex: 'lastModified',
      key: 'lastModified',
      sorter: (a, b) => (a.lastModified || '').localeCompare(b.lastModified || ''),
      className: 'sort-space-none',
      render: (val) => <Text label={val || '—'} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />,
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: () => (
        <StatusBadge status="submitted">
          <CheckCircleFilled style={{ fontSize: '12px' }} />
          Submitted
        </StatusBadge>
      ),
    },
  ];

  const outstandingColumns = [
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'client',
      sorter: (a, b) => a.client.localeCompare(b.client),
      className: 'sort-space-none',
      render: (text, record) => (
        <div>
          <Text label={text} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />
          <Text label={record.ssnortin} fontSize={`${theme.font_size.size_l}`} color={`${theme.colors.grey_tertiary}`} lineheight="18px" />
        </div>
      ),
    },
    {
      title: '# Holdings',
      dataIndex: 'holdings',
      key: 'holdings',
      sorter: (a, b) => (a.holdings ?? 0) - (b.holdings ?? 0),
      className: 'sort-space-none',
      render: (val) => <Text label={val != null ? String(val) : '—'} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />,
    },
    {
      title: 'Total Balance',
      dataIndex: 'totalBalance',
      key: 'totalBalance',
      sorter: (a, b) => (a.totalBalance ?? 0) - (b.totalBalance ?? 0),
      defaultSortOrder: 'descend',
      className: 'sort-space-none',
      render: (val) => <Text label={formatCurrency(val)} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />,
    },
    {
      title: 'Last Modified',
      dataIndex: 'lastModified',
      key: 'lastModified',
      sorter: (a, b) => (a.lastModified || '').localeCompare(b.lastModified || ''),
      className: 'sort-space-none',
      render: (val) => <Text label={val || '—'} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />,
    },
    {
      title: 'Status',
      dataIndex: 'submissionStatus',
      key: 'submissionStatus',
      width: 150,
      render: (val) => {
        const badgeStatus = val === 'Partial' ? 'outstanding' : 'notsubmitted';
        const icon = val === 'Partial'
          ? <ClockCircleFilled style={{ fontSize: '12px' }} />
          : <ExclamationCircleFilled style={{ fontSize: '12px' }} />;
        return (
          <StatusBadge status={badgeStatus}>
            {icon}
            {val || '—'}
          </StatusBadge>
        );
      },
    },
  ];

  if (webuserAccessType !== 1 && webuserAccessType !== 2) return null;

  const periodLabel = `${MONTHS.find(m => m.value === selectedMonth)?.label} / ${selectedYear}`;

  return (
    <AppLayout>
      <Banner bgcolor={primary_color} height="124px">
        <Box
          display="flex"
          alignitems="center"
          height="100%"
          minwidth="100%"
          width="100%"
          zindex="3"
          position="relative"
          padding="44px 0 36px 0"
        >
          <Container>
            <Box display="flex" justifycontent="space-between" alignitems="flex-start" margin="0">
              <Box flex="1">
                <Text
                  color={`${theme.colors.white}`}
                  fontSize="36px"
                  lineheight="44px"
                  label="All Third-Party Holdings"
                />
                <Text
                  color={`rgba(255,255,255,0.75)`}
                  fontSize={`${theme.font_size.size_xl}`}
                  lineheight="22px"
                  label="Submission status dashboard for internal use"
                />
              </Box>
              <Box display="flex" alignitems="center" gap="12px">
                <ButtonAnt
                  type="link"
                  label="Download"
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  style={{ color: theme.colors.white }}
                />
              </Box>
            </Box>
          </Container>
        </Box>
      </Banner>

      <ContentWrapper rgbaHover={rgbaPrimary}>
        <Container>
          <Wrapper>
            <BackLink onClick={() => history.push('/third-party-holdings')}>
              <ArrowLeftOutlined />
              Back to Third-Party Holdings
            </BackLink>

            {/* ── Period selector ── */}
            <PeriodBar>
              <PeriodLabel>Period:</PeriodLabel>
              <Select
                value={selectedMonth}
                onChange={(val) => { setSelectedMonth(val); setPagination((p) => ({ ...p, current: 1 })); }}
                style={{ width: 80 }}
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 1060 }}
              >
                {MONTHS.map(({ value, label }) => (
                  <Option key={value} value={value}>{label}</Option>
                ))}
              </Select>
              <Select
                value={selectedYear}
                onChange={(val) => { setSelectedYear(val); setPagination((p) => ({ ...p, current: 1 })); }}
                style={{ width: 90 }}
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 1060 }}
              >
                {YEARS.map((y) => <Option key={y} value={y}>{y}</Option>)}
              </Select>
              <span style={{ marginLeft: '8px', color: theme.colors.grey_tertiary, fontSize: theme.font_size.size_l, fontFamily: 'Inter', display: 'inline-flex', alignItems: 'center' }}>
                Showing submission status for period {periodLabel}
                <Tooltip
                  placement="top"
                  title="This page shows 3 months of data: the selected month and the 2 previous months."
                  overlayClassName="tooltip"
                >
                  <InfoCircleOutlined style={{ marginLeft: '8px', color: `${theme.colors.blue_primay}`, fontSize: '16px', cursor: 'pointer' }} />
                </Tooltip>
              </span>
            </PeriodBar>

            {/* ── Summary stat strip ── */}
            <StatStrip>
              <StatCard>
                <StatCardHeader>
                  <TeamOutlined style={{ fontSize: '18px', color: primary_color }} />
                  <Text label="Submission Progress" fontSize={`${theme.font_size.size_l}`} color={`${theme.colors.grey_tertiary}`} lineheight="20px" />
                </StatCardHeader>
                <StatValue color={primary_color}>{totalSubmitted} / {totalClients}</StatValue>
                <StatSubLabel>clients submitted for {periodLabel}</StatSubLabel>
                <Progress
                  percent={completionPct}
                  strokeColor={primary_color}
                  trailColor={theme.colors.light_grey}
                  showInfo
                  size="small"
                  style={{ margin: '4px 0 0 0' }}
                />
              </StatCard>

              <StatCard>
                <StatCardHeader>
                  <DollarOutlined style={{ fontSize: '18px', color: '#1e7e34' }} />
                  <Text label="Total Balance Submitted" fontSize={`${theme.font_size.size_l}`} color={`${theme.colors.grey_tertiary}`} lineheight="20px" />
                </StatCardHeader>
                <StatValue color="#1e7e34">{formatCurrency(totalBalance)}</StatValue>
                <StatSubLabel>across {totalSubmitted} submitted clients</StatSubLabel>
              </StatCard>

              <StatCard>
                <StatCardHeader>
                  <ClockCircleFilled style={{ fontSize: '18px', color: '#856404' }} />
                  <Text label="Outstanding Clients" fontSize={`${theme.font_size.size_l}`} color={`${theme.colors.grey_tertiary}`} lineheight="20px" />
                </StatCardHeader>
                <StatValue color='#856404'>
                  {totalOutstandingClients}
                </StatValue>
              </StatCard>
            </StatStrip>

            {/* ── Single table with inline tab toggles ── */}
            <TableComponent
              className="table-primary"
              title={
                <Box
                  display="flex"
                  justifycontent="space-between"
                  alignitems="center"
                  padding="16px 24px"
                  border={`1px solid ${theme.colors.grey_fossil}`}
                  borderwidth="0 0 1px 0"
                >
                  <InputComponent
                    type="search"
                    placeholder="Search clients..."
                    maxLength={100}
                    value={activeTab === 'submitted' ? submittedSearch : outstandingSearch}
                    onInput={(e) => {
                      const v = e.target.value;
                      activeTab === 'submitted' ? setSubmittedSearch(v) : setOutstandingSearch(v);
                      setPagination((p) => ({ ...p, current: 1 }));
                    }}
                    onSearch={(v) => {
                      activeTab === 'submitted' ? setSubmittedSearch(v) : setOutstandingSearch(v);
                      setPagination((p) => ({ ...p, current: 1 }));
                    }}
                  />
                  <ToolbarControls>
                    <TabToggleGroup>
                      <TabToggleBtn
                        active={activeTab === 'submitted'}
                        onClick={() => { setActiveTab('submitted'); setPagination((p) => ({ ...p, current: 1 })); }}
                      >
                        <CheckCircleFilled style={{ fontSize: '12px', color: activeTab === 'submitted' ? '#1e7e34' : theme.colors.grey_tertiary }} />
                        Submitted
                        <Tag
                          color={activeTab === 'submitted' ? 'green' : 'default'}
                          style={{ marginLeft: 2, borderRadius: 20, fontSize: 11, lineHeight: '16px', padding: '0 6px' }}
                        >
                          {totalSubmitted}
                        </Tag>
                      </TabToggleBtn>
                      <TabToggleBtn
                        active={activeTab === 'outstanding'}
                        onClick={() => { setActiveTab('outstanding'); setPagination((p) => ({ ...p, current: 1 })); }}
                      >
                        <ClockCircleFilled style={{ fontSize: '12px', color: activeTab === 'outstanding' ? '#856404' : theme.colors.grey_tertiary }} />
                        In Progress
                        <Tag
                          color={activeTab === 'outstanding' ? 'warning' : 'default'}
                          style={{ marginLeft: 2, borderRadius: 20, fontSize: 11, lineHeight: '16px', padding: '0 6px' }}
                        >
                          {outstandingClients.length}
                        </Tag>
                      </TabToggleBtn>
                    </TabToggleGroup>
                  </ToolbarControls>
                </Box>
              }
              columns={activeTab === 'submitted' ? submittedColumns : outstandingColumns}
              loading={loading}
              dataSource={activeTab === 'submitted' ? finalSubmitted : finalOutstanding}
              expandable={{
                expandedRowRender: (record) => (
                  <Table
                    showHeader={false}
                    columns={holdingColumns}
                    dataSource={record.holdingRows}
                    pagination={false}
                    rowKey="key"
                    locale={{ emptyText: <NoDataFound text="No holdings found" /> }}
                  />
                ),
                rowExpandable: (record) => record.holdingRows && record.holdingRows.length > 0,
                expandedRowKeys: expandedKeys,
                onExpand: (expanded, record) => {
                  setExpandedKeys((prev) =>
                    expanded ? [...prev, record.key] : prev.filter((k) => k !== record.key)
                  );
                },
                expandIcon: ({ expanded, onExpand, record }) =>
                  record.holdingRows && record.holdingRows.length > 0 ? (
                    <ExpandButton
                      className={expanded ? 'expanded' : ''}
                      onClick={(e) => { e.stopPropagation(); onExpand(record, e); }}
                    >
                      <span />
                    </ExpandButton>
                  ) : null,
              }}
              locale={{
                emptyText: (
                  <NoDataFound
                    text={
                      activeTab === 'submitted'
                        ? 'No clients have submitted for this period yet'
                        : 'All clients have submitted for this period'
                    }
                  />
                ),
              }}
              showSorterTooltip={false}
              pagination={pagination}
              onChange={(e) => setPagination((p) => ({ ...p, current: e.current }))}
            />
          </Wrapper>
        </Container>
      </ContentWrapper>
    </AppLayout>
  );
};

export default AllThirdPartyHoldingsContainer;
